import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import classNames from 'classnames';
import Image from 'components/gruenland/atoms/image';
import Prices from 'components/gruenland/molecules/product-tile/prices';
import useShopable from 'helpers/hooks/useShopable';
import { TYPOGRAPHY_PRODUCT_CARD_DESCRIPTION, TYPOGRAPHY_PRODUCT_CARD_TITLE } from 'lib/typography';
import { productTileImage } from 'lib/wardaImages';
import { Product } from 'types/entity/product';
import OutOfStock from '../../badge/OutOfStock';
import Promo from '../../badge/Promo';
import Link from '../../link';

interface Props {
  hit: Product;
  onClick?: () => void;
}

const SearchItem: React.FC<Props> = ({ hit, onClick }) => {
  const router = useRouter();
  const variant = useMemo(() => hit.variants?.[0], [hit]);

  const handleClick = useCallback(() => {
    onClick?.();
    router.push(hit._url ?? '#');
  }, [hit, onClick, router]);

  const hasDiscount = useMemo(
    () =>
      variant.discountedPrice?.centAmount &&
      variant.price?.centAmount &&
      variant.discountedPrice.centAmount < variant.price.centAmount,
    [variant],
  );
  const discountedPrice = variant?.discountedPrice;
  const shopable = useShopable();

  return (
    <Link link={hit._url} onClick={handleClick}>
      <div className="flex items-center gap-3">
        <div className="shrink-0 p-2">
          <div className="relative h-[90px] w-[80px]">
            <Image src={productTileImage(variant?.images)} suffix="small" style={{ objectFit: 'contain' }} fill />
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className={classNames(TYPOGRAPHY_PRODUCT_CARD_TITLE)}>
              {variant?.attributes?.structure} {hit?.productKey}
            </p>
            {hasDiscount && <Promo />}
            {!variant.isOnStock && <OutOfStock />}
          </div>
          <p className={TYPOGRAPHY_PRODUCT_CARD_DESCRIPTION}>{hit.name}</p>
          {shopable && (
            <div className="mt-2">
              <Prices price={variant?.price} discountedPrice={discountedPrice} />
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default SearchItem;
